import { useResourceStorageKeys } from "src/framework/useResourceStorageKeys.tsx";
import { useEffect } from "react";
import { memoryStorageDriver } from "src/storage/memoryStorage.driver.ts";
import { normalizePathname } from "src/lib/tabs/normalizePathname.ts";
import type { Tab } from "src/framework/form/TabsFormLayoutV2.tsx";
import type { TabsStore } from "src/lib/tabs-ui/TabsStore.tsx";
import { replacePathParams } from "@exonn/utils";
import { matchRoutes } from "react-router-dom";
import type { Router } from "@remix-run/router";

export const useSubscribeTabsToFormUnsavedChanges = ({
  resource,
  tabs,
  type,
  id,
  tabsStore,
  paths,
  router,
}: {
  resource: string;
  type: "edit" | "create";
  id: string | undefined;
  tabsStore: TabsStore;
  tabs: Tab[];
  paths: string[];
  router: Router;
}) => {
  const storageKeys = useResourceStorageKeys({
    resource: resource,
  });

  useEffect(() => {
    const cleanFns = tabs.flatMap((tab, index) => {
      const storageKey = storageKeys.form({
        id: id!,
        type,
        tab: tab.key,
      });

      const unsub = memoryStorageDriver.on("change", storageKey, (value) => {
        const hasUnsavedChanges = !!value;

        const params = paths.map((path, index) => {
          const matches = matchRoutes(router.routes, path) || [];

          for (let i = 0; i < matches.length; i++) {
            if (matches[i].route.path === tabs[index].path) {
              return matches[i].params;
            }
          }

          return {};
        });

        const tabId = normalizePathname(
          replacePathParams(tab.path, params[index]),
        );
        const storeTab = tabsStore.tabs.find((tab) => tab.id === tabId);
        if (storeTab) {
          storeTab.setHasUnsavedChanges(hasUnsavedChanges);
        }
      });
      return [unsub];
    });

    return () => {
      cleanFns.forEach((cleanFn) => cleanFn());
    };
  }, [id, tabs, type, tabsStore, storageKeys, paths, router.routes]);
};
